import { useEffect, useRef, useState, useCallback } from 'react';
import { Button, Modal } from 'core-ui';
import Cookies from 'js-cookie';
import {
  ACCESS_TOKEN_KEY,
  CUSTOMER_ACCESS_TOKEN_KEY
} from 'src/constants/auth';
import { RoutePath } from 'src/enums/routePath';

interface UseSessionTimeoutProps {
  timeoutMinutes?: number;
  warningMinutes?: number;
}

// Debug keys exposed in localStorage so testers can verify session state
// directly from DevTools > Application > Local Storage on staging / prod.
const STORAGE_KEY_REMAINING = 'session-time-remaining-sec';
const STORAGE_KEY_EXPIRES_AT = 'session-expires-at';
const STORAGE_KEY_LAST_RESET = 'session-last-reset';
const STORAGE_KEY_ACTIVITY = 'session-activity'; // cross-tab sync trigger

export const useSessionTimeout = ({
  timeoutMinutes = 30, // default 30 minutes
  warningMinutes = 0.5 // default 1 minute
}: UseSessionTimeoutProps = {}) => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [hasToken, setHasToken] = useState(
    () =>
      !!Cookies.get(ACCESS_TOKEN_KEY) ||
      !!Cookies.get(CUSTOMER_ACCESS_TOKEN_KEY)
  );

  const expiresAtRef = useRef<number>(0);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastResetRef = useRef(Date.now());
  const THROTTLE_TIME = 5000;
  const timeoutMs = timeoutMinutes * 60 * 1000;
  const warningMs = warningMinutes * 60 * 1000;

  useEffect(() => {
    const checkToken = () => {
      const tokenExists =
        !!Cookies.get(ACCESS_TOKEN_KEY) ||
        !!Cookies.get(CUSTOMER_ACCESS_TOKEN_KEY);
      setHasToken(tokenExists);
    };

    const intervalId = setInterval(checkToken, 1000);
    return () => clearInterval(intervalId);
  }, []);

  const clearDebugStorage = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY_REMAINING);
    localStorage.removeItem(STORAGE_KEY_EXPIRES_AT);
    localStorage.removeItem(STORAGE_KEY_LAST_RESET);
  }, []);

  const logout = useCallback(() => {
    const currentPath = window.location.pathname;
    const isAdmin = currentPath.startsWith('/admin');
    const isCustomer = currentPath.startsWith('/customer');

    if (tickRef.current) clearInterval(tickRef.current);
    clearDebugStorage();

    if (isAdmin) {
      Cookies.remove(ACCESS_TOKEN_KEY);
      window.location.replace(RoutePath.ADMIN + RoutePath.LOGIN);
    } else if (isCustomer) {
      Cookies.remove(CUSTOMER_ACCESS_TOKEN_KEY);
      window.location.replace(RoutePath.CUSTOMER + RoutePath.LOGIN);
    }
  }, [clearDebugStorage]);

  const resetExpiry = useCallback(
    (broadcast = true) => {
      const now = Date.now();
      const expiresAt = now + timeoutMs;
      expiresAtRef.current = expiresAt;
      lastResetRef.current = now;
      setShowWarning(false);

      localStorage.setItem(STORAGE_KEY_LAST_RESET, new Date(now).toISOString());
      localStorage.setItem(
        STORAGE_KEY_EXPIRES_AT,
        new Date(expiresAt).toISOString()
      );
      localStorage.setItem(
        STORAGE_KEY_REMAINING,
        String(Math.ceil(timeoutMs / 1000))
      );

      if (broadcast) {
        localStorage.setItem(STORAGE_KEY_ACTIVITY, String(now));
      }
    },
    [timeoutMs]
  );

  const extendSession = useCallback(() => {
    resetExpiry();
  }, [resetExpiry]);

  useEffect(() => {
    if (!hasToken) {
      if (tickRef.current) clearInterval(tickRef.current);
      clearDebugStorage();
      return;
    }

    const events: (keyof DocumentEventMap)[] = [
      'mousedown',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastResetRef.current > THROTTLE_TIME) {
        resetExpiry();
      }
    };

    resetExpiry(false);

    // Timestamp-based tick: each second, compare expiresAt against Date.now().
    // This is robust to tab throttling / machine sleep — even if the tick
    // is delayed, the next firing will detect expiry correctly.
    tickRef.current = setInterval(() => {
      const now = Date.now();
      const remainingMs = expiresAtRef.current - now;
      const remainingSec = Math.max(0, Math.ceil(remainingMs / 1000));

      localStorage.setItem(STORAGE_KEY_REMAINING, String(remainingSec));

      if (remainingMs <= 0) {
        logout();
        return;
      }

      if (remainingMs <= warningMs) {
        setShowWarning(true);
        setTimeRemaining(remainingSec);
      }
    }, 1000);

    events.forEach((event) =>
      document.addEventListener(event, handleActivity, true)
    );

    return () => {
      events.forEach((event) =>
        document.removeEventListener(event, handleActivity, true)
      );
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [hasToken, resetExpiry, logout, clearDebugStorage, warningMs]);

  useEffect(() => {
    const handleCrossTabActivity = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY_ACTIVITY && hasToken) {
        resetExpiry(false);
      }
    };
    window.addEventListener('storage', handleCrossTabActivity);
    return () => window.removeEventListener('storage', handleCrossTabActivity);
  }, [resetExpiry, hasToken]);

  // When tab returns from background / machine wakes from sleep, force an
  // immediate expiry check instead of waiting up to a second for the tick.
  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden && hasToken && expiresAtRef.current > 0) {
        if (Date.now() >= expiresAtRef.current) {
          logout();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibility);
  }, [hasToken, logout]);

  const warningModal = (
    <Modal
      title="セッションの有効期限が近づいています"
      open={showWarning}
      closable={false}
      footer={
        <div className="flex justify-end">
          <Button onClick={extendSession} type="primary">
            セッションを延長
          </Button>
        </div>
      }
      width={400}
    >
      <p>
        セッションがまもなく期限切れになります。 残り時間:{' '}
        {Math.floor(timeRemaining / 60)}分{timeRemaining % 60}秒
      </p>
      <p>セッションを延長するか、ログアウトしてください。</p>
    </Modal>
  );

  return {
    warningModal
  };
};
